import { ReactNode, createContext, useState } from 'react';
import { Role } from 'src/Api/Enum/Role';
import useAuth from 'src/hooks/useAuth';
import { axiosMethod, useAxiosV2 } from 'src/hooks/useAxiosV2';

// ----------------------------------------------------------------------

export type DocumentStatus = 'uploaded' | 'missing' | 'pending';

export type PrivacyDocument = {
  id: string;
  name: string;
  type: 'LIA' | 'DPIA' | 'informativa_semplificata' | 'informativa_estesa' | 'general' | 'custom';
  version?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  status: DocumentStatus;
  fileUrl?: string;
};

export type PrivacyTemplate = {
  id: string;
  name: string;
  type: 'LIA' | 'DPIA' | 'informativa_semplificata' | 'informativa_estesa';
  language: string;
  version?: string;
  uploadedAt?: string;
  fileUrl?: string;
};

export type FAQItem = {
  id: string;
  question: string;
  answer: string;
  category: 'servizi' | 'privacy';
};

type PrivacyContextProps = {
  officialDocuments: PrivacyDocument[];
  templates: PrivacyTemplate[];
  faqItems: FAQItem[];
  uploadingTemplateId: string | null;
  uploadSuccess: string | null;
  uploadError: string | null;
  isAdminRole: boolean;
  isSuperUserRole: boolean;
  isPlannerAnalystRole: boolean;
  onHandleDownloadDocument: (doc: PrivacyDocument | PrivacyTemplate) => void;
  onHandleUploadTemplate: (templateId: string, file: File) => Promise<void>;
  onHandleCreateDocument: (name: string, file?: File) => Promise<void>;
  onHandleSendContactEmail: (data: ContactFormData) => Promise<void>;
  contactSending: boolean;
  contactSuccess: boolean;
  contactError: string | undefined;
};

export type ContactFormData = {
  name: string;
  email: string;
  company: string;
  subject: string;
  message: string;
};

// --- Helpers ---

function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

// --- Mock Data ---

const MOCK_OFFICIAL_DOCUMENTS: PrivacyDocument[] = [
  {
    id: 'doc-lia-1',
    name: 'LIA – Legitimate Interest Assessment',
    type: 'LIA',
    version: '2.1',
    uploadedBy: 'Blimp S.r.l.',
    uploadedAt: '12/12/2024',
    status: 'uploaded',
    fileUrl: '#',
  },
  {
    id: 'doc-dpia-1',
    name: 'DPIA – Data Protection Impact Assessment',
    type: 'DPIA',
    version: '3.0',
    uploadedBy: 'Blimp S.r.l.',
    uploadedAt: '12/12/2024',
    status: 'uploaded',
    fileUrl: '#',
  },
  {
    id: 'doc-info-semplificata-1',
    name: 'Informativa Privacy Semplificata',
    type: 'informativa_semplificata',
    version: '1.1',
    uploadedBy: 'Blimp S.r.l.',
    uploadedAt: '05/09/2024',
    status: 'uploaded',
    fileUrl: '#',
  },
  {
    id: 'doc-info-estesa-1',
    name: 'Informativa Privacy Estesa',
    type: 'informativa_estesa',
    version: '1.0',
    uploadedBy: 'Blimp S.r.l.',
    uploadedAt: '05/09/2024',
    status: 'uploaded',
    fileUrl: '#',
  },
  {
    id: 'doc-tos-1',
    name: 'General Terms of Service',
    type: 'general',
    version: '1.4',
    uploadedBy: 'Blimp S.r.l.',
    uploadedAt: '01/06/2024',
    status: 'uploaded',
    fileUrl: '#',
  },
];

const MOCK_TEMPLATES: PrivacyTemplate[] = [
  {
    id: 'tpl-lia-it',
    name: 'LIA Assessment Template – Versione Italiana',
    type: 'LIA',
    language: 'IT',
    version: '1.3',
    uploadedAt: '10/10/2024',
    fileUrl: '#',
  },
  {
    id: 'tpl-lia-en',
    name: 'LIA Assessment Template – English version',
    type: 'LIA',
    language: 'EN',
    version: '1.3',
    uploadedAt: '10/10/2024',
    fileUrl: '#',
  },
  {
    id: 'tpl-dpia-it',
    name: 'DPIA Standard Template – Versione Italiana',
    type: 'DPIA',
    language: 'IT',
    version: '2.1',
    uploadedAt: '12/12/2024',
    fileUrl: '#',
  },
  {
    id: 'tpl-dpia-en',
    name: 'DPIA Standard Template – English version',
    type: 'DPIA',
    language: 'EN',
    version: '2.1',
    uploadedAt: '12/12/2024',
    fileUrl: '#',
  },
  {
    id: 'tpl-info-semplificata-it',
    name: 'Informativa Semplificata – Versione Italiana',
    type: 'informativa_semplificata',
    language: 'IT',
    version: '1.1',
    uploadedAt: '05/09/2024',
    fileUrl: '#',
  },
  {
    id: 'tpl-info-semplificata-en',
    name: 'Simplified Privacy Notice – English version',
    type: 'informativa_semplificata',
    language: 'EN',
    version: '1.1',
    uploadedAt: '05/09/2024',
    fileUrl: '#',
  },
  {
    id: 'tpl-info-estesa-it',
    name: 'Informativa Estesa – Versione Italiana',
    type: 'informativa_estesa',
    language: 'IT',
    version: '1.0',
    uploadedAt: '05/09/2024',
    fileUrl: '#',
  },
  {
    id: 'tpl-info-estesa-en',
    name: 'Full Privacy Notice – English version',
    type: 'informativa_estesa',
    language: 'EN',
    version: '1.0',
    uploadedAt: '05/09/2024',
    fileUrl: '#',
  },
];

const FAQ_ITEMS: FAQItem[] = [
  {
    id: 'faq-1',
    question: "Che cos'è il servizio Blimp Analytics?",
    answer: "Blimp Analytics è il nostro servizio di monitoraggio dei flussi di persone, composto da Head-Counter (sensore) e da Audience Platform (software in cloud).",
    category: 'servizi',
  },
  {
    id: 'faq-2',
    question: "Il servizio Blimp Analytics acquisisce dati personali?",
    answer: "Sì. Per ottenere i dati statistici, il servizio Blimp Analytics acquisisce, tramite i sensori Head-Counter, immagini di individui passanti nel raggio di azione. Le fotografie vivono, per pochi secondi, solo ed esclusivamente sulla memoria volatile del dispositivo, per permettere l'esecuzione dell'algoritmo di reti neurali che consente di estrapolare i soli dati statistici e anonimi. Una volta estrapolati i dati statistici, le immagini vengono eliminate in maniera permanente.",
    category: 'privacy',
  },
  {
    id: 'faq-3',
    question: "Che documenti/autorizzazioni servono per installare l'Head-Counter?",
    answer: "Gli Head-Counter necessitano della affissione di un'informativa privacy semplificata, da esporre in un luogo accessibile al pubblico (es. una vetrofania per un negozio) e il collegamento ad una informativa privacy estesa. In caso di dubbi su dove esporre l'informativa contattaci a privacy@blimp.ai.",
    category: 'privacy',
  },
  {
    id: 'faq-4',
    question: "Qual è la base giuridica del trattamento per il Servizio Blimp Analytics?",
    answer: "La base giuridica del trattamento per il servizio Blimp Analytics è il legittimo interesse (art. 6 c. 1 lett. f del GDPR).",
    category: 'privacy',
  },
  {
    id: 'faq-5',
    question: "Il servizio Blimp Analytics viola la privacy delle persone?",
    answer: "No. Il Servizio Blimp Analytics permette di monitorare flussi di persone dai quali deduciamo dei dati di carattere generale e statistico senza identificare i singoli individui.",
    category: 'privacy',
  },
  {
    id: 'faq-6',
    question: "Che cos'è una DPIA e perché ne ho bisogno?",
    answer: "Una DPIA (Data Protection Impact Assessment) è un documento richiesto dall'articolo 35 del GDPR in alcuni casi. Ha lo scopo di mappare tutti i trattamenti di dati personali che possono comportare un rischio elevato per i diritti e le libertà delle persone fisiche. La DPIA deve essere predisposta dal Titolare del Trattamento dei Dati.",
    category: 'privacy',
  },
  {
    id: 'faq-7',
    question: "C'è differenza tra l'Head-Counter e una telecamera di videosorveglianza?",
    answer: "Sì. La differenza si sostanzia nello scopo: le telecamere di videosorveglianza svolgono una funzione di prevenzione e monitoraggio di un'area; l'Head-Counter, invece, svolge una mera funzione di conteggio di flussi di persone e di alcuni altri parametri a fini statistici.",
    category: 'servizi',
  },
  {
    id: 'faq-8',
    question: "Per installare Head-Counter in un negozio serve un accordo sindacale?",
    answer: "No. È comunque consigliabile, ma non obbligatorio, informare i dipendenti della presenza di questa tecnologia e fornire chiarimenti, qualora richiesti, in merito al funzionamento della stessa.",
    category: 'privacy',
  },
  {
    id: 'faq-9',
    question: "Quando usufruisco del servizio Blimp Analytics sono Titolare del Trattamento?",
    answer: "Sì, quando utilizzi gli Head-Counter di Blimp sarai identificato come Titolare del trattamento dei Dati: questo perché sarai responsabile nel determinare gli scopi e i mezzi del trattamento dei Dati Personali raccolti attraverso i dispositivi.",
    category: 'privacy',
  },
  {
    id: 'faq-10',
    question: "Blimp ha nominato un DPO?",
    answer: "Sì. Il nostro DPO è contattabile all'indirizzo privacy@blimp.ai.",
    category: 'privacy',
  },
  {
    id: 'faq-11',
    question: "Che cos'è l'Head-Counter?",
    answer: "L'Head-Counter è un sensore in grado di rilevare in tempo reale alcuni dati aggregati e statistici inerenti alle persone che rientrano nel suo raggio d'azione. Nella sua versione standard è in grado di rilevare individui fino a 60 metri di distanza.",
    category: 'servizi',
  },
  {
    id: 'faq-12',
    question: "Che documenti/autorizzazioni servono per installare l'Head-Counter? (dettaglio normativo)",
    answer: "Uniformandosi alle disposizioni del Provvedimento del Garante per la Protezione dei Dati Personali n. 551 del 21 dicembre 2017, gli Head-Counter necessitano della affissione di un'informativa privacy semplificata, da esporre in un luogo accessibile al pubblico (es. una vetrofania per un negozio) e il collegamento ad una informativa privacy estesa. In caso di dubbi su dove esporre l'informativa contattaci a privacy@blimp.ai per ricevere assistenza.",
    category: 'privacy',
  },
  {
    id: 'faq-13',
    question: "Il servizio Blimp Analytics mette a repentaglio i \"diritti e le libertà delle persone fisiche\"?",
    answer: "No.\n\nSe hai tempo, lasciaci spiegare un po' meglio.\n\nIl Reg. UE 679/2016 (c.d. \"GDPR\") definisce un elenco di casistiche entro le quali i \"diritti e le libertà\" delle persone fisiche potrebbero essere messi a repentaglio. Ciò che la normativa impone è che, laddove ci sia anche solo un minimo sospetto di minaccia ai cittadini UE, il Titolare del Trattamento della tecnologia \"indaghi\" quali rischi si potrebbero verificare e implementi delle soluzioni per mitigarli. Queste attività dovrebbero essere compiute in un processo chiamato Data Protection Impact Assessment (\"DPIA\").\n\nSecondo l'art. 35, paragrafo 3 del GDPR, sono in particolare tre le casistiche entro le quali occorre procedere ad una DPIA:\n- una valutazione sistematica e globale di aspetti personali relativi a persone fisiche, basata su un trattamento automatizzato, compresa la profilazione;\n- il trattamento, su larga scala, di categorie particolari di dati personali;\n- la sorveglianza sistematica su larga scala di una zona accessibile al pubblico.\n\nCome Blimp.ai, pur non operando come Titolari del Trattamento, abbiamo condotto e revisioniamo regolarmente la nostra DPIA, prestando particolare attenzione a tutti i rischi e le minacce che potrebbero verificarsi per i diritti e le libertà dei cittadini UE.",
    category: 'privacy',
  },
  {
    id: 'faq-14',
    question: "Che cos'è Audience Platform?",
    answer: "La Audience Platform è un software dedicato alla visualizzazione ed analisi dei dati misurati tramite Head-Counter.",
    category: 'servizi',
  },
  {
    id: 'faq-15',
    question: "La Audience Platform si basa su tecnologia Cloud?",
    answer: "Sì, per il salvataggio e la visualizzazione dei dati statistici e anonimi inviati dai singoli dispositivi Head-Counter utilizza i servizi di cloud computing di Microsoft Azure.\n\nÈ importante chiarire che non è il dispositivo ad essere basato sulla tecnologia cloud, quanto la sola piattaforma Analytics.",
    category: 'servizi',
  },
  {
    id: 'faq-16',
    question: "Dove può essere installato un Head-Counter?",
    answer: "Gli Head-Counter possono essere installati sia all'aperto (piazze, strade etc.) che al chiuso (es. negozi, contesti aziendali, ecc.).",
    category: 'servizi',
  },
  {
    id: 'faq-17',
    question: "Come verifichiamo il corretto funzionamento degli Head-Counter?",
    answer: "Operiamo dei controlli semestrali con specifico riguardo al Camera Sensor e alla memoria locale della Main Board, volti ad appurare eventuali malfunzionamenti, indisponibilità degli apparati o tentativi di accesso fraudolento. A tale scopo, le attività di manutenzione sono riportate in apposito registro delle manutenzioni.",
    category: 'servizi',
  },
  {
    id: 'faq-18',
    question: "Che cos'è l'interesse legittimo?",
    answer: "L'interesse legittimo del Titolare del Trattamento è una delle condizioni (basi giuridiche) previste dalla normativa sulla protezione dei dati personali per poter procedere ad un trattamento di dati personali.\n\nSe il trattamento è basato su interesse legittimo non occorre il consenso dell'interessato, purché però non prevalgano gli interessi o i diritti e le libertà fondamentali dell'interessato, tenuto conto delle ragionevoli aspettative dello stesso in base alla relazione col titolare del trattamento (cfr. Considerando 47 del Regolamento Generale europeo).",
    category: 'privacy',
  },
  {
    id: 'faq-19',
    question: "È possibile monitorare le persone tramite Head-Counter?",
    answer: "Assolutamente no. Il sistema non è sviluppato per tenere traccia degli spostamenti delle persone né, tantomeno, per monitorarne le attività. La tecnologia Head-Counter si limita ad effettuare una valutazione della quantità di persone presenti in un determinato luogo e in un determinato momento, estraendo alcuni dati statistici.",
    category: 'privacy',
  },
  {
    id: 'faq-20',
    question: "C'è differenza tra l'Head-Counter e una telecamera di videosorveglianza? (dettaglio)",
    answer: "Sì. La differenza si sostanzia nello scopo: le telecamere di videosorveglianza svolgono una funzione di prevenzione e monitoraggio di un'area; l'Head-Counter, invece, svolge una mera funzione di conteggio di flussi di persone e di alcuni altri parametri a fini statistici. Per questo scopo intrinseco non è necessario procedere alla comunicazione del dispositivo alle rappresentanze Sindacali.",
    category: 'privacy',
  },
  {
    id: 'faq-21',
    question: "L'Head-Counter salva immagini o video?",
    answer: "No, le immagini che vengono scattate rimangono nella memoria volatile del dispositivo per il solo tempo dell'elaborazione da parte degli algoritmi e vengono prontamente cancellate non appena terminato il processo.",
    category: 'privacy',
  },
  {
    id: 'faq-22',
    question: "L'Head-Counter invia immagini sul cloud?",
    answer: "No, le immagini permangono esclusivamente all'interno dei dispositivi per il solo tempo necessario ad estrapolare i dati statistici necessari (pochi secondi). Nessuna immagine viene inviata a fini di conservazione in cloud.",
    category: 'privacy',
  },
  {
    id: 'faq-23',
    question: "Che cos'è un'informativa estesa?",
    answer: "L'informativa sul trattamento dei dati personali ha lo scopo di fornire all'interessato tutte le informazioni previste dalla normativa applicabile, in particolare dal Regolamento (UE) 2016/679 (\"GDPR\"). Essa descrive in modo chiaro e trasparente le modalità e le finalità del trattamento, la base giuridica, le categorie di dati trattati, i soggetti coinvolti, i tempi di conservazione, nonché i diritti riconosciuti agli interessati e le modalità per esercitarli.",
    category: 'privacy',
  },
  {
    id: 'faq-24',
    question: "Che cos'è un'informativa semplificata?",
    answer: "Si tratta di un documento sintetico che riporta le informazioni essenziali relative al trattamento dei dati. È possibile rilasciare una informativa semplificata pur mantenendo un rimando a quella completa sul sito.",
    category: 'privacy',
  },
  {
    id: 'faq-25',
    question: "Quali misure di sicurezza implementiamo per gli Head-Counter?",
    answer: "Gli Head-Counter adottano misure di sicurezza informatica di alto livello e stringenti: tra queste annoveriamo la cancellazione automatica delle immagini senza memorizzazione, criteri severi per la scelta delle password di accesso, cifratura delle informazioni, oscuramento delle immagini durante attività di manutenzione e molte altre.",
    category: 'privacy',
  },
  {
    id: 'faq-26',
    question: "Si può accedere ai dispositivi Head-Counter?",
    answer: "Sì, ma solo per eseguire operazioni di manutenzione, in maniera molto limitata e se autorizzati. Tutti gli accessi, protetti da credenziali, sono tracciati tramite un apposito registro.",
    category: 'privacy',
  },
  {
    id: 'faq-27',
    question: "Le attività sugli Head-Counter sono loggate?",
    answer: "Sì, ogni operazione effettuata sui dispositivi Head-Counter viene generato un file di log che traccia le attività.",
    category: 'privacy',
  },
  {
    id: 'faq-28',
    question: "L'Head-Counter è protetto contro manomissioni?",
    answer: "Il dispositivo è composto da una scocca blindata al fine di impedire smontaggi e manomissioni. Inoltre, il dispositivo è dotato di sigilli di garanzia per la verifica di eventuali tentativi di manomissione.",
    category: 'privacy',
  },
  {
    id: 'faq-29',
    question: "Blimp.ai usa credenziali di autenticazione?",
    answer: "Sì.",
    category: 'privacy',
  },
  {
    id: 'faq-30',
    question: "Qual è il rapporto tra Blimp e i propri clienti con riguardo alla normativa GDPR?",
    answer: "Blimp.ai è Responsabile del trattamento dei dati raccolti, mentre il Cliente (il business che implementa la tecnologia di Blimp.ai) è il Titolare del trattamento dei dati raccolti.",
    category: 'privacy',
  },
  {
    id: 'faq-31',
    question: "Chi è il Titolare del Trattamento dei Dati?",
    answer: "Il Titolare del Trattamento dei dati è la \"persona fisica o giuridica, l'autorità pubblica, il servizio o altro organismo che, singolarmente o insieme ad altri, determina le finalità e i mezzi del trattamento di dati personali\" (Vedi art. 4(7) GDPR).",
    category: 'privacy',
  },
  {
    id: 'faq-32',
    question: "Chi è il Responsabile del Trattamento dei Dati?",
    answer: "Il Responsabile del Trattamento dei Dati è la persona fisica o giuridica, l'autorità pubblica, il servizio o qualsiasi altro organismo che tratta dati personali per conto del titolare del trattamento.",
    category: 'privacy',
  },
  {
    id: 'faq-33',
    question: "Che cos'è una DPIA e perché ne ho bisogno per installare e utilizzare Head-Counter?",
    answer: "Una DPIA (Data Protection Impact Assessment) è un documento richiesto dall'articolo 35 del GDPR in alcuni casi. Essa ha lo scopo di mappare tutti i trattamenti di dati personali che possono comportare un rischio elevato per i diritti e le libertà delle persone fisiche, cittadine dell'Unione Europea. La DPIA deve essere predisposta dal Titolare del Trattamento dei Dati.",
    category: 'privacy',
  },
  {
    id: 'faq-34',
    question: "Blimp.ai sarà in grado di vedere i dati (fotografie) raccolti dagli Head-Counter?",
    answer: "Blimp.ai è in grado di vedere le fotografie raccolte dagli Head-Counter ma non i dati personali perché le fotografie arrivano già sfocate sul volto, rendendo impossibile l'identificazione. Il Titolare del trattamento deve incaricare contrattualmente Blimp S.r.l. come Responsabile del Trattamento dei Dati.",
    category: 'privacy',
  },
  {
    id: 'faq-35',
    question: "In quanto cliente e Titolare del Trattamento, devo scrivere una DPIA da zero?",
    answer: "La redazione di una DPIA, per i servizi di Blimp.ai, è altamente consigliata. In quanto Titolare del Trattamento è tuo onere procedere alla sua predisposizione e ogni DPIA deve essere progettata e modellata a seconda dello specifico caso di implementazione. Come tuoi fornitori possiamo fornirti alcune istruzioni tramite un modello di DPIA che puoi completare autonomamente. Se hai bisogno di ulteriori informazioni, non esitare a contattarci all'indirizzo privacy@blimp.ai.",
    category: 'privacy',
  },
  {
    id: 'faq-36',
    question: "Per poter utilizzare il servizio Blimp Analytics devo fare una comunicazione al Garante per la Protezione dei Dati Personali?",
    answer: "No, non è necessario. Tutti i rischi sono stati mappati e non c'è bisogno di procedere ad una consultazione preventiva. Per ricevere informazioni in merito alla nostra DPIA, non esitare a contattarci all'indirizzo privacy@blimp.ai.",
    category: 'privacy',
  },
];

const initialState: PrivacyContextProps = {
  officialDocuments: MOCK_OFFICIAL_DOCUMENTS,
  templates: MOCK_TEMPLATES,
  faqItems: FAQ_ITEMS,
  uploadingTemplateId: null,
  uploadSuccess: null,
  uploadError: null,
  isAdminRole: false,
  isSuperUserRole: false,
  isPlannerAnalystRole: false,
  onHandleDownloadDocument: () => { },
  onHandleUploadTemplate: async () => { },
  onHandleCreateDocument: async () => { },
  onHandleSendContactEmail: async () => { },
  contactSending: false,
  contactSuccess: false,
  contactError: undefined,
};

const PrivacyContext = createContext<PrivacyContextProps>(initialState);

type PrivacyProviderProps = {
  children: ReactNode;
};

function PrivacyProvider({ children }: PrivacyProviderProps) {
  const { user } = useAuth();

  const isSuperUserRole = !!(user && user.roles.includes(Role.SUPER_USER));

  // Admin = SUPER_ADMIN, ADMIN, PARTNER, SUPER_USER (store manager)
  const isAdminRole = !!(
    user &&
    (user.roles.includes(Role.SUPER_ADMIN) ||
      user.roles.includes(Role.ADMIN) ||
      user.roles.includes(Role.PARTNER) ||
      user.roles.includes(Role.SUPER_USER))
  );

  const isPlannerAnalystRole = !!(
    user && (user.roles.includes(Role.PLANNER) || user.roles.includes(Role.ANALYST))
  );

  const [officialDocuments, setOfficialDocuments] = useState<PrivacyDocument[]>(MOCK_OFFICIAL_DOCUMENTS);
  const [templates, setTemplates] = useState<PrivacyTemplate[]>(MOCK_TEMPLATES);
  const [uploadingTemplateId, setUploadingTemplateId] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const {
    axiosFetch: sendContactEmailRequest,
    loaded: contactLoaded,
    success: contactSuccess,
    error: contactError,
    setError: setContactError,
  } = useAxiosV2<void>();

  const contactSending = !contactLoaded;

  const handleDownloadDocument = (doc: PrivacyDocument | PrivacyTemplate) => {
    if (!doc.fileUrl) return;
    const link = document.createElement('a');
    link.href = doc.fileUrl;
    link.download = doc.name;
    link.click();
  };

  // Used for both templates and official documents: ids are unique across the two lists,
  // so the item matching the id gets updated wherever it lives.
  const handleUploadTemplate = async (templateId: string, file: File) => {
    setUploadingTemplateId(templateId);
    setUploadSuccess(null);
    setUploadError(null);
    try {
      // TODO: replace with the real upload API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const formattedDate = formatDate(new Date());
      const fileUrl = URL.createObjectURL(file);

      setTemplates((prev) =>
        prev.map((t) => (t.id === templateId ? { ...t, uploadedAt: formattedDate, fileUrl } : t))
      );
      setOfficialDocuments((prev) =>
        prev.map((d) =>
          d.id === templateId ? { ...d, uploadedAt: formattedDate, fileUrl, status: 'uploaded' } : d
        )
      );

      setUploadSuccess(templateId);
      setTimeout(() => setUploadSuccess(null), 3000);
    } catch {
      setUploadError(templateId);
      setTimeout(() => setUploadError(null), 3000);
    } finally {
      setUploadingTemplateId(null);
    }
  };

  // SuperUser only: adds a new custom row to the Documents section.
  // Throws on failure so the calling row can show its own error message.
  const handleCreateDocument = async (name: string, file?: File) => {
    if (!isSuperUserRole) {
      throw new Error('Only SuperUser can create documents');
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      throw new Error('Document name is required');
    }

    // TODO: replace with the real create API call (e.g. POST /privacy/documents with FormData)
    await new Promise((resolve) => setTimeout(resolve, 800));

    const newDocument: PrivacyDocument = {
      id: `doc-custom-${Date.now()}`,
      name: trimmedName,
      type: 'custom',
      uploadedAt: file ? formatDate(new Date()) : undefined,
      status: file ? 'uploaded' : 'missing',
      fileUrl: file ? URL.createObjectURL(file) : undefined,
    };

    setOfficialDocuments((prev) => [...prev, newDocument]);
  };

  const handleSendContactEmail = async (data: ContactFormData) => {
    setContactError(undefined);

    await sendContactEmailRequest({
      url: '/privacy/sendmail',
      method: axiosMethod.post,
      payload: {
        sendFrom: `${data.email}`,
        company: data.company || 'Blimp',
        mailObject: data.subject,
        MailBody: [
          `Nome: ${data.name}`,
          `Email: ${data.email}`,
          data.company ? `Azienda: ${data.company}` : null,
          '',
          data.message,
        ]
          .filter(Boolean)
          .join('\n'),
      },
    });
  };

  return (
    <PrivacyContext.Provider
      value={{
        officialDocuments,
        templates,
        faqItems: FAQ_ITEMS,
        uploadingTemplateId,
        uploadSuccess,
        uploadError,
        isAdminRole,
        isSuperUserRole,
        isPlannerAnalystRole,
        onHandleDownloadDocument: handleDownloadDocument,
        onHandleUploadTemplate: handleUploadTemplate,
        onHandleCreateDocument: handleCreateDocument,
        onHandleSendContactEmail: handleSendContactEmail,
        contactSending,
        contactSuccess: !!contactSuccess,
        contactError,
      }}
    >
      {children}
    </PrivacyContext.Provider>
  );
}

export { PrivacyContext, PrivacyProvider };